// Global Instructions Rule Applied!
//
// Phase 4b — TeamDetailDrawer.
//
// Opens from the team-mode Recipients grid (one row per account). Lets the
// user reshape a single team and (optionally) author a per-team override
// of subject + body that will be sent ONCE to that team's recipients.
//
// Anatomy:
//   Header:   Account name + (team_name) tag + recipient count
//   Left:     To list   (role dropdown + Remove per row)
//             Cc list   (role dropdown + Remove per row)
//             Add picker (search account contacts not already in this team)
//   Right:    Subject override input
//             Body override editor (Jodit)
//             Live preview (ShadowRichHtml) rendered with team-mode merge
//             values so the user sees the actual greeting + account name
//             that will land in the inbox.
//
// Persistence: every action calls a hook mutator that POSTs to the backend
// and refetches the staged grid. Errors are surfaced via toast. No
// in-drawer dirty buffer for add/remove/role — those are eager writes;
// only the override editor batches its subject + body via a Save button
// so the user can iterate on the text without N round-trips.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Col,
  Divider,
  Drawer,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import { DeleteOutlined, UndoOutlined, CheckCircleOutlined } from '@ant-design/icons'
import MergeFieldsButton from './MergeFieldsButton.tsx'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import type { EmailRecipientGridModel } from '../../../state_manager/data_models/email_recipient_grid_model.ts'
import type { TeamCandidateContact } from '../services/emailService.ts'
import RichTextEditor from '../../../core/components/RichTextEditor.jsx'
import type { CKEditorRichTextHandle } from '../../../core/components/CKEditorRichText.tsx'
import { useEditorEngine, useEditorConfigReady } from '../../global_configs/hooks/useEditorConfig.ts'
import { ShadowRichHtml } from '../../../core/components/ShadowRichHtml.tsx'
import ReadOnlyFileList, { type ReadOnlyFile } from '../../../core/components/ReadOnlyFileList.tsx'

const { Text, Title } = Typography

// Lazy-loaded (code-split; editor build loads from the CDN). Mirrors Full.tsx.
const CKEditorRichText = React.lazy(
  () => import('../../../core/components/CKEditorRichText.tsx')
)

interface TeamDetailDrawerProps {
  open:                  boolean
  accountId:             string | null
  accountName:           string
  accountTeamName:       string                       // accounts.team_name; empty when blank
  recipients:            EmailRecipientGridModel[]    // members of this team (non-trashed)
  campaignSubject:       string                       // campaign-level subject (preview fallback)
  campaignBodyHtml:      string                       // campaign-level body (preview fallback)
  locked:                boolean                      // when status != draft, all edits are inert
  // Per-account attachments (capital call notices, stage 5): the files only
  // THIS account receives. Undefined = the campaign carries none, and the
  // card is not shown; an empty list = it does and this account has none.
  accountFiles?:         ReadOnlyFile[]
  // Mutators (hook-bound; resolve to null on success or an error string on failure).
  onRoleChange:          (recipientId: string, role: 'to' | 'cc') => Promise<string | null>
  onRemove:              (recipientId: string)                    => Promise<string | null>
  onAdd:                 (contactId: string, role: 'to' | 'cc')   => Promise<string | null>
  onSaveOverride:        (payload: { subject_override: string | null; body_override_html: string | null }) => Promise<string | null>
  onSearchCandidates:    (term: string)                           => Promise<TeamCandidateContact[]>
  onClose:               () => void
}

const ROLE_OPTIONS = [
  { value: 'to', label: 'To' },
  { value: 'cc', label: 'Cc' }
]

/**
 * Compute the team-level {{first_names_list}} value the same way the edge
 * function does (Phase 4b): comma-join the first_names of 'to' recipients,
 * dropping blanks. Keeps the live preview honest about what the team
 * message will look like.
 */
function firstNamesListOf(recipients: EmailRecipientGridModel[]): string {
  return recipients
    .filter((r) => r.email_recipients__role === 'to')
    .map((r) => {
      const md = r.email_recipients__merge_data
      if (md && typeof md === 'object' && !Array.isArray(md)) {
        const v = (md as Record<string, unknown>).first_name
        if (v === null || v === undefined) return ''
        return String(v).trim()
      }
      return ''
    })
    .filter((s) => s !== '')
    .join(', ')
}

/**
 * Edge-fn-parallel substitution: {{key}} → string from data; unknown keys
 * collapse to empty. Whitespace inside the braces is tolerated. We mirror
 * the dispatch's behaviour here so the preview matches what Postmark
 * actually receives.
 */
function substituteMergeFields(template: string, data: Record<string, unknown>): string {
  if (!template) return ''
  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, key: string) => {
    const v = data[key]
    if (v === null || v === undefined) return ''
    return String(v)
  })
}

export default function TeamDetailDrawer({
  open,
  accountId,
  accountName,
  accountTeamName,
  recipients,
  campaignSubject,
  campaignBodyHtml,
  locked,
  accountFiles,
  onRoleChange,
  onRemove,
  onAdd,
  onSaveOverride,
  onSearchCandidates,
  onClose
}: TeamDetailDrawerProps) {
  const [modalApi, modalContextHolder] = Modal.useModal()

  // Override edit buffer. Initialized when the drawer (re)opens:
  //   - existing override present (any row in the team carries it; the
  //     controller writes uniformly) → load that
  //   - no override → pre-fill with the campaign defaults so the user can
  //     edit a copy rather than start blank
  // openSnapshot freezes the campaign defaults at open time so the Save
  // handler can detect "unchanged from campaign" and persist NULL/NULL
  // instead of a redundant snapshot copy (which would otherwise freeze
  // the team to today's campaign content even when the user later edits
  // the campaign body on the Compose tab).
  const [subject, setSubject] = useState<string>('')
  const [body, setBody]       = useState<string>('')
  // Live mirror of the body for the preview pane — RichTextEditor.onLiveChange
  // updates it on every keystroke, while `body` (save/value source) settles on
  // blur. Without it the preview wouldn't move until the editor lost focus.
  const [livePreviewBody, setLivePreviewBody] = useState<string>('')
  // Editor engine (cfg-driven). 'ckeditor' swaps in CKEditor and inline-at-
  // saves the override body (it's the final sent body for this team).
  const editorEngine = useEditorEngine()
  // Gate the editor until globalConfigs loads (avoids a cold-load Jodit flash).
  const engineReady = useEditorConfigReady()
  const editorRef = useRef<CKEditorRichTextHandle | null>(null)
  const [openSnapshot, setOpenSnapshot] = useState<{ subject: string; body: string }>({
    subject: '',
    body:    ''
  })
  const [savingOverride, setSavingOverride] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  // Add-picker state (AutoComplete).
  const [pickerSearch, setPickerSearch] = useState('')
  const [candidates, setCandidates] = useState<TeamCandidateContact[]>([])
  const [candidatesLoading, setCandidatesLoading] = useState(false)
  const [pickerRole, setPickerRole] = useState<'to' | 'cc'>('to')

  // Pull the team's current override (uniform across rows) OR pre-fill
  // with the campaign defaults when no override is set. Snapshot the
  // campaign defaults so handleSaveOverride can detect "no edit relative
  // to campaign" and persist NULL (no override).
  useEffect(() => {
    if (!open) return
    const first             = recipients[0]
    const existingSubject   = first?.email_recipients__subject_override ?? ''
    const existingBody      = first?.email_recipients__body_override_html ?? ''
    const initialBody       = existingBody || campaignBodyHtml || ''
    setSubject(existingSubject || campaignSubject || '')
    setBody(initialBody)
    setLivePreviewBody(initialBody)
    setOpenSnapshot({ subject: campaignSubject || '', body: campaignBodyHtml || '' })
    setPickerSearch('')
    setCandidates([])
    setPickerRole('to')
  }, [open, recipients, campaignSubject, campaignBodyHtml])

  // Sorted team rows: 'to' first, then 'cc', then by email asc — mirrors
  // the dispatch's representative-pick order so the displayed "Team
  // greeting first name source" stays in sync.
  const sortedRecipients = useMemo(() => {
    const arr = [...recipients]
    arr.sort((a, b) => {
      if (a.email_recipients__role !== b.email_recipients__role) {
        return a.email_recipients__role === 'to' ? -1 : 1
      }
      return (a.email_recipients__email_address || '').localeCompare(
        b.email_recipients__email_address || ''
      )
    })
    return arr
  }, [recipients])

  const toRows = sortedRecipients.filter((r) => r.email_recipients__role === 'to')
  const ccRows = sortedRecipients.filter((r) => r.email_recipients__role === 'cc')

  // Preview merge values — exactly the team-mode shape the edge fn builds.
  const teamName = accountTeamName?.trim() || accountName
  const previewMergeData: Record<string, unknown> = useMemo(() => {
    const rep = toRows[0]
    const repMerge = rep && rep.email_recipients__merge_data &&
      typeof rep.email_recipients__merge_data === 'object' &&
      !Array.isArray(rep.email_recipients__merge_data)
      ? (rep.email_recipients__merge_data as Record<string, unknown>)
      : {}
    return {
      ...repMerge,
      first_name:       '',
      last_name:        '',
      email:            '',
      team_name:        teamName,
      first_names_list: firstNamesListOf(recipients),
      account_name:     accountName
    }
  }, [recipients, toRows, teamName, accountName])

  const previewSubject = substituteMergeFields(
    (subject?.trim() ? subject : campaignSubject) || '',
    previewMergeData
  )
  const previewHtml = substituteMergeFields(
    (livePreviewBody?.trim() ? livePreviewBody : campaignBodyHtml) || '',
    previewMergeData
  )

  // ── Add picker ───────────────────────────────────────────────────────────

  const refreshCandidates = useCallback(
    async (term: string) => {
      if (!accountId) {
        setCandidates([])
        return
      }
      setCandidatesLoading(true)
      try {
        const list = await onSearchCandidates(term)
        setCandidates(list)
      } finally {
        setCandidatesLoading(false)
      }
    },
    [accountId, onSearchCandidates]
  )

  // Refresh candidates whenever the drawer opens (with empty term) or the
  // team membership changes (since added/removed contacts shift the
  // available candidate set).
  useEffect(() => {
    if (open) refreshCandidates('')
  }, [open, recipients.length, refreshCandidates])

  const onPickerSearch = useCallback(
    (value: string) => {
      setPickerSearch(value)
      // Debounce-light: refresh on every keypress; backend is local and
      // the candidate set is small (per-account).
      refreshCandidates(value)
    },
    [refreshCandidates]
  )

  const onPickerSelect = useCallback(
    async (contactId: string) => {
      const opt = candidates.find((c) => c.value === contactId)
      if (!opt) return
      setAdding(true)
      try {
        const err = await onAdd(contactId, pickerRole)
        if (err) {
          showErrorToast(err)
          return
        }
        toast.success(`Added ${opt.label} to this team`)
        setPickerSearch('')
      } finally {
        setAdding(false)
      }
    },
    [candidates, onAdd, pickerRole]
  )

  // ── Per-row actions ──────────────────────────────────────────────────────

  const handleRoleChange = useCallback(
    async (recipientId: string, role: 'to' | 'cc') => {
      const err = await onRoleChange(recipientId, role)
      if (err) showErrorToast(err)
    },
    [onRoleChange]
  )

  const handleRemove = useCallback(
    (recipientId: string, email: string) => {
      modalApi.confirm({
        title: `Remove ${email} from this team?`,
        content:
          'This soft-deletes the row from the campaign. You can re-resolve or re-add the contact later.',
        okText: 'Remove',
        okButtonProps: { danger: true },
        onOk: async () => {
          setRemovingId(recipientId)
          try {
            const err = await onRemove(recipientId)
            if (err) showErrorToast(err)
            else toast.success(`Removed ${email}`)
          } finally {
            setRemovingId(null)
          }
        }
      })
    },
    [modalApi, onRemove]
  )

  // ── Override save ────────────────────────────────────────────────────────

  const handleSaveOverride = useCallback(async () => {
    setSavingOverride(true)
    try {
      // If the editor still holds the campaign default verbatim (i.e. the
      // user opened the drawer and didn't touch the field), clear the column
      // so this team keeps tracking the live campaign default at dispatch
      // time. Empty editor → also clear. We send '' (NOT null): the backend
      // maps '' → NULL (clear) and null → "leave the column alone". Sending
      // null for both builds an empty UPDATE, which the controller rejects
      // with a 500 "Nothing to update" — and null would never clear an
      // existing override on revert.
      const subjectIsDefault = subject === openSnapshot.subject || !subject?.trim()
      const bodyIsDefault    = body    === openSnapshot.body    || !body?.trim()
      const persistedSubject = subjectIsDefault ? '' : subject
      let   persistedBody    = bodyIsDefault ? '' : body
      // Inline-at-save (plan §6.1) only on the persist path: the override is
      // the final sent body for this team, so a CKEditor body must be baked
      // inline. Untouched bodies stay === the campaign default (CKEditor
      // doesn't fire change on load) → cleared above, never re-inlined.
      if (persistedBody && editorEngine === 'ckeditor') {
        try {
          const inline = await editorRef.current?.getInlineHtml()
          if (inline && inline.trim()) persistedBody = inline
        } catch {
          /* keep the class-based body fallback */
        }
      }

      const err = await onSaveOverride({
        subject_override:   persistedSubject,
        body_override_html: persistedBody
      })
      if (err) {
        showErrorToast(err)
        return
      }
      if (subjectIsDefault && bodyIsDefault) {
        toast.success('No override saved — this team uses the campaign body and subject.')
      } else {
        toast.success('Team override saved')
      }
      // Close on a successful save to match the individual override drawer.
      onClose()
    } finally {
      setSavingOverride(false)
    }
  }, [onSaveOverride, subject, body, openSnapshot, editorEngine, onClose])

  const handleClearOverride = useCallback(() => {
    modalApi.confirm({
      title: 'Clear the team override?',
      content:
        'This team will receive the campaign-level subject and body on send. Existing override is removed.',
      okText: 'Clear',
      okButtonProps: { danger: true },
      onOk: async () => {
        setSavingOverride(true)
        try {
          // Empty string (not null) clears the columns server-side; null would
          // be a no-op (and an all-null payload 500s as "Nothing to update").
          const err = await onSaveOverride({ subject_override: '', body_override_html: '' })
          if (err) showErrorToast(err)
          else {
            toast.success('Team override cleared')
            // Re-pre-fill the editor with the live campaign defaults so
            // the user immediately sees what'll be sent to this team.
            setSubject(campaignSubject || '')
            setBody(campaignBodyHtml || '')
            setLivePreviewBody(campaignBodyHtml || '')
          }
        } finally {
          setSavingOverride(false)
        }
      }
    })
  }, [modalApi, onSaveOverride, campaignSubject, campaignBodyHtml])

  // ── Render ───────────────────────────────────────────────────────────────

  if (!accountId) return null

  // For the AutoComplete options the value must be unique; we use contact_id.
  const pickerOptions = candidates.map((c) => ({ value: c.value, label: c.label }))

  return (
    <Drawer
      title={
        <Space wrap>
          <Title level={5} style={{ margin: 0 }}>
            {accountName || '(no account)'}
          </Title>
          {accountTeamName ? <Tag color='blue'>team: {accountTeamName}</Tag> : null}
          <Tag color={recipients.length > 0 ? 'green' : 'default'}>
            {recipients.length} recipient{recipients.length === 1 ? '' : 's'}
          </Tag>
          {toRows.length > 0 && <Tag>{toRows.length} To</Tag>}
          {ccRows.length > 0 && <Tag>{ccRows.length} Cc</Tag>}
        </Space>
      }
      placement='right'
      width={1180}
      open={open}
      onClose={onClose}
      destroyOnClose
      footer={
        <Space style={{ float: 'right' }}>
          <Button
            onClick={handleClearOverride}
            icon={<DeleteOutlined />}
            danger
            disabled={locked || savingOverride}
          >
            Clear overrides
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type='primary'
            onClick={handleSaveOverride}
            loading={savingOverride}
            disabled={locked}
          >
            Save
          </Button>
        </Space>
      }
    >
      {modalContextHolder}

      {locked && (
        <Alert
          type='warning'
          showIcon
          style={{ marginBottom: 12 }}
          message='Campaign is locked'
          description='This campaign is past draft — recipients and overrides can no longer be edited.'
        />
      )}

      <Row gutter={24} align='stretch'>
        {/* ── Left: Manage addressees (eager) ──────────────────────────── */}
        <Col xs={24} md={11} style={{ borderRight: '1px solid #f0f0f0' }}>
          <Title level={5} style={{ marginTop: 0, marginBottom: 6 }}>
            Manage addressees
          </Title>
          <Tag icon={<CheckCircleOutlined />} color='success' style={{ marginBottom: 12 }}>
            Saved immediately upon change
          </Tag>
          <Card title='To recipients' size='small' style={{ marginBottom: 12 }}>
            {toRows.length === 0 ? (
              <Text type='secondary'>(none)</Text>
            ) : (
              <Space direction='vertical' style={{ width: '100%' }} size={6}>
                {toRows.map((r) => (
                  <RecipientRow
                    key={r.email_recipients__id}
                    row={r}
                    locked={locked}
                    removing={removingId === r.email_recipients__id}
                    onChangeRole={(role) => handleRoleChange(r.email_recipients__id, role)}
                    onRemove={() => handleRemove(r.email_recipients__id, r.email_recipients__email_address)}
                  />
                ))}
              </Space>
            )}
          </Card>

          <Card title='Cc recipients' size='small' style={{ marginBottom: 12 }}>
            {ccRows.length === 0 ? (
              <Text type='secondary'>(none)</Text>
            ) : (
              <Space direction='vertical' style={{ width: '100%' }} size={6}>
                {ccRows.map((r) => (
                  <RecipientRow
                    key={r.email_recipients__id}
                    row={r}
                    locked={locked}
                    removing={removingId === r.email_recipients__id}
                    onChangeRole={(role) => handleRoleChange(r.email_recipients__id, role)}
                    onRemove={() => handleRemove(r.email_recipients__id, r.email_recipients__email_address)}
                  />
                ))}
              </Space>
            )}
          </Card>

          <Card title='Add contact to this team' size='small'>
            <Space style={{ width: '100%' }} direction='vertical'>
              <Space>
                <Text type='secondary'>Role:</Text>
                <Select
                  size='small'
                  value={pickerRole}
                  options={ROLE_OPTIONS}
                  style={{ width: 90 }}
                  onChange={(v) => setPickerRole(v as 'to' | 'cc')}
                  disabled={locked || adding}
                />
              </Space>
              <AutoComplete
                style={{ width: '100%' }}
                options={pickerOptions}
                value={pickerSearch}
                onSearch={onPickerSearch}
                onChange={(v) => setPickerSearch(typeof v === 'string' ? v : '')}
                onSelect={onPickerSelect}
                placeholder={
                  candidatesLoading
                    ? 'Searching…'
                    : candidates.length === 0
                    ? 'No more contacts available on this account'
                    : 'Type to filter; select to add'
                }
                disabled={locked || adding}
                allowClear
                notFoundContent={candidatesLoading ? <Spin size='small' /> : 'No match'}
              />
              <Text type='secondary' style={{ fontSize: 12 }}>
                Suggestions exclude contacts already in this team. Add picker uses contacts
                attached to {accountName} via contact_accounts.
              </Text>
            </Space>
          </Card>

          {accountFiles ? (
            <Card title='Attachments for this account' size='small' style={{ marginTop: 12 }}>
              {/* A file opens in a viewer above this drawer, which stays
                  open - closing it would drop an unsaved override. */}
              <ReadOnlyFileList
                files={accountFiles}
                emptyText='No attachment for this account. The campaign cannot be queued without one.'
              />
            </Card>
          ) : null}
        </Col>

        {/* ── Right: Message Override (batched, saved on Save) ─────────── */}
        <Col xs={24} md={13}>
          <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
            Message Override
          </Title>
          <Card size='small' style={{ marginBottom: 12 }}>
            <Form layout='vertical'>
              <Form.Item
                label='Subject'
                help='Pre-filled from the campaign subject. Edit to customize for this team; reset (or leave it unchanged) to keep tracking the live campaign subject.'
              >
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                  <Button
                    type='link'
                    size='small'
                    icon={<UndoOutlined />}
                    onClick={() => setSubject(openSnapshot.subject)}
                    disabled={locked || subject === openSnapshot.subject}
                    style={{ padding: 0 }}
                  >
                    Reset to campaign subject
                  </Button>
                </div>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={campaignSubject || '(no campaign subject)'}
                  disabled={locked}
                  allowClear
                  maxLength={500}
                />
              </Form.Item>
              <Form.Item
                label='Body'
                help='Pre-filled from the campaign body. Edit to customize for this team; reset (or leave it unchanged) to keep tracking the live campaign body. Sanitized server-side on save and applied to every row of this team.'
              >
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 4 }}>
                  <MergeFieldsButton type='link' />
                  <Button
                    type='link'
                    size='small'
                    icon={<UndoOutlined />}
                    onClick={() => {
                      setBody(openSnapshot.body)
                      setLivePreviewBody(openSnapshot.body)
                    }}
                    disabled={locked || livePreviewBody === openSnapshot.body}
                    style={{ padding: 0 }}
                  >
                    Reset to campaign body
                  </Button>
                </div>
                <div style={{ height: 320 }}>
                  {!engineReady ? (
                    <div style={{ padding: 24, textAlign: 'center' }}>
                      <Spin />
                    </div>
                  ) : editorEngine === 'ckeditor' ? (
                    <React.Suspense
                      fallback={
                        <div style={{ padding: 24, textAlign: 'center' }}>
                          <Spin />
                        </div>
                      }
                    >
                      <CKEditorRichText
                        ref={editorRef}
                        value={body}
                        onChange={setBody}
                        onLiveChange={setLivePreviewBody}
                        placeholder='Type a per-team body…'
                        imageUpload={false}
                      />
                    </React.Suspense>
                  ) : (
                    <RichTextEditor
                      value={body}
                      onChange={setBody}
                      onLiveChange={setLivePreviewBody}
                      placeholder='Type a per-team body…'
                    />
                  )}
                </div>
              </Form.Item>
            </Form>
          </Card>

          <Card title='Preview (this team)' size='small'>
            <Card size='small' style={{ marginBottom: 8 }}>
              <Text type='secondary' style={{ fontSize: 12 }}>Subject:</Text>{' '}
              <Text>{previewSubject || '(empty)'}</Text>
            </Card>
            <Divider style={{ margin: 0 }} />
            <ShadowRichHtml
              html={previewHtml || '<p style="color:#bfbfbf">(empty body)</p>'}
              className='block'
            />
            <Text type='secondary' style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
              Preview uses the team-mode merge values: <Text code>{'{{team_name}}'}</Text> = {teamName || '(blank)'},{' '}
              <Text code>{'{{first_names_list}}'}</Text> = {firstNamesListOf(recipients) || '(blank)'},{' '}
              <Text code>{'{{account_name}}'}</Text> = {accountName}. Contact-level fields
              (<Text code>{'{{first_name}}'}</Text>, <Text code>{'{{last_name}}'}</Text>, <Text code>{'{{email}}'}</Text>) render empty in team mode.
            </Text>
          </Card>
        </Col>
      </Row>
    </Drawer>
  )
}

interface RecipientRowProps {
  row:          EmailRecipientGridModel
  locked:       boolean
  removing:     boolean
  onChangeRole: (role: 'to' | 'cc') => void
  onRemove:     () => void
}

function RecipientRow({ row, locked, removing, onChangeRole, onRemove }: RecipientRowProps) {
  const name = `${row.email_recipients__contact_first_name ?? ''} ${row.email_recipients__contact_last_name ?? ''}`.trim()
  return (
    <Row align='middle' gutter={8} wrap={false}>
      <Col flex='auto' style={{ minWidth: 0 }}>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <Text>{name || row.email_recipients__email_address}</Text>{' '}
          {name && (
            <Text type='secondary' style={{ fontSize: 12 }}>
              · {row.email_recipients__email_address}
            </Text>
          )}
        </div>
      </Col>
      <Col flex='none'>
        <Select
          size='small'
          value={row.email_recipients__role}
          options={ROLE_OPTIONS}
          style={{ width: 70 }}
          onChange={(v) => onChangeRole(v as 'to' | 'cc')}
          disabled={locked}
        />
      </Col>
      <Col flex='none'>
        <Tooltip title='Remove from this team'>
          <Button
            size='small'
            type='text'
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
            loading={removing}
            disabled={locked}
          />
        </Tooltip>
      </Col>
    </Row>
  )
}
